// src/pages/Accounting/Reports/AccountList/components/CustomizeVisualTab.tsx

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { AVAILABLE_COLUMNS } from '../constants';
import type { ReportCustomization } from '../types';

interface CustomizeVisualTabProps {
  draft: ReportCustomization;
  updateDraft: (patch: Partial<ReportCustomization>) => void;
}

type Align = 'left' | 'center' | 'right';

export default function CustomizeVisualTab({ draft, updateDraft }: CustomizeVisualTabProps) {
  const setHeader = (patch: Partial<ReportCustomization['header']>) =>
    updateDraft({ header: { ...draft.header, ...patch } });
  const setFooter = (patch: Partial<ReportCustomization['footer']>) =>
    updateDraft({ footer: { ...draft.footer, ...patch } });
  const setCell = (patch: Partial<ReportCustomization['cell']>) =>
    updateDraft({ cell: { ...draft.cell, ...patch } });
  const setColColor = (key: string, color: string) =>
    updateDraft({ columnColors: { ...draft.columnColors, [key]: color } });
  const setRowBand = (color: string) =>
    updateDraft({ rowSettings: { ...draft.rowSettings, bandedRowColor: color } });
  const setGridlines = (border: 'default' | 'none' | 'strong') =>
    updateDraft({ gridlines: { ...draft.gridlines, borderStyle: border } });

  return (
    <div>
      {/* Header */}
      <SectionHeader title="Header" />
      <Checkbox label="Company logo" checked={draft.header.showCompanyLogo} onChange={(v) => setHeader({ showCompanyLogo: v })} />
      <Checkbox label="Company name" checked={draft.header.showCompanyName} onChange={(v) => setHeader({ showCompanyName: v })} />
      <Checkbox label="Report title" checked={draft.header.showReportTitle} onChange={(v) => setHeader({ showReportTitle: v })} />
      <Checkbox label="Report period" checked={draft.header.showReportPeriod} onChange={(v) => setHeader({ showReportPeriod: v })} />
      <FieldLabel className="mt-2">Alignment</FieldLabel>
      <RadioRow name="headerAlign" value={draft.header.alignment} onChange={(v) => setHeader({ alignment: v as Align })} options={[['left', 'Left'], ['center', 'Centre'], ['right', 'Right']]} />

      {/* Footer */}
      <SectionHeader title="Footer" className="mt-5" />
      <Checkbox label="Date prepared" checked={draft.footer.showDatePrepared} onChange={(v) => setFooter({ showDatePrepared: v })} />
      <Checkbox label="Time prepared" checked={draft.footer.showTimePrepared} onChange={(v) => setFooter({ showTimePrepared: v })} />
      <FieldLabel className="mt-2">Alignment</FieldLabel>
      <RadioRow name="footerAlign" value={draft.footer.alignment} onChange={(v) => setFooter({ alignment: v as Align })} options={[['left', 'Left'], ['center', 'Centre'], ['right', 'Right']]} />

      {/* Cell settings */}
      <SectionHeader title="Cell settings" className="mt-5" />
      <FieldLabel>Show empty cells as</FieldLabel>
      <RadioRow name="emptyCell" value={draft.cell.emptyCellStyle} onChange={(v) => setCell({ emptyCellStyle: v as 'dash' | 'blank' })} options={[['dash', 'Dash'], ['blank', 'Blank']]} />

      {/* Column settings */}
      <SectionHeader title="Column settings" className="mt-5" />
      <div className="text-[11.5px] text-ink3 font-semibold mb-2">Click a dot to set the column header colour.</div>
      {AVAILABLE_COLUMNS.map((c) => {
        const color = draft.columnColors[c.key as string] || '';
        return (
          <div key={c.key as string} className="flex items-center justify-between py-1.5 text-[12px] text-ink">
            <span>{c.label}</span>
            <input type="color" value={color || '#ffffff'} onChange={(e) => setColColor(c.key as string, e.target.value)} className="w-5 h-5 border border-line rounded-full cursor-pointer p-0" />
          </div>
        );
      })}

      {/* Row settings */}
      <SectionHeader title="Row settings" className="mt-5" />
      <div className="flex items-center justify-between py-1.5 text-[12px] text-ink">
        <span>Banded row colour</span>
        <input type="color" value={draft.rowSettings.bandedRowColor || '#ffffff'} onChange={(e) => setRowBand(e.target.value)} className="w-5 h-5 border border-line rounded-full cursor-pointer p-0" />
      </div>

      {/* Gridlines */}
      <SectionHeader title="Gridlines" className="mt-5" />
      <FieldLabel>Report borders</FieldLabel>
      <div className="relative mb-2">
        <select value={draft.gridlines.borderStyle} onChange={(e) => setGridlines(e.target.value as 'default' | 'none' | 'strong')} className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
          <option value="default">Default</option>
          <option value="none">None</option>
          <option value="strong">Strong</option>
        </select>
        <ChevronDown size={11} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}

function SectionHeader({ title, className = '' }: { title: string; className?: string }) {
  return <div className={`text-[13px] font-bold text-ink mb-2 ${className}`}>{title}</div>;
}
function FieldLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <label className={`text-[11px] font-semibold text-ink3 block mb-1.5 ${className}`}>{children}</label>;
}
function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 py-1 text-[12px] text-ink cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-teal-dark" />
      {label}
    </label>
  );
}
function RadioRow({ name, value, onChange, options }: { name: string; value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  return (
    <div className="flex gap-5 mb-2">
      {options.map(([v, label]) => (
        <label key={v} className="inline-flex items-center gap-1.5 text-[12px] text-ink cursor-pointer">
          <input type="radio" name={name} checked={value === v} onChange={() => onChange(v)} className="accent-teal-dark" />
          {label}
        </label>
      ))}
    </div>
  );
}